// IndexedDB local chat cache — Phase 1: Admin only.
//
// A durable, on-device database so recently-opened rooms paint INSTANTLY even
// after the app is fully closed / reopened, or with no network at all — like
// WhatsApp keeping recent history on-device.
//
// Rules (see spec):
//  • Server is always the final authority — this is a fast local PREVIEW only;
//    the latest server response always overwrites the cache.
//  • Text / JSON only. Media (images/videos) already live as object-storage
//    URLs — we cache only the URL reference, never the binary file.
//  • 5-day local retention (server keeps full history forever).
//  • Everything is wrapped in try/catch — if IndexedDB is unavailable
//    (incognito / old browser) we silently fall back to network-only. Never
//    fatal.

const DB_NAME = 'dws_local_chat';
const DB_VERSION = 1;
const MSG_STORE = 'messages';
const ROOM_STORE = 'rooms';
const RETENTION_MS = 5 * 24 * 60 * 60 * 1000; // 5 days

let _dbPromise = null;

const openDB = () => {
  if (_dbPromise) return _dbPromise;
  _dbPromise = new Promise((resolve) => {
    try {
      if (typeof indexedDB === 'undefined') { resolve(null); return; }
      const req = indexedDB.open(DB_NAME, DB_VERSION);
      req.onupgradeneeded = (e) => {
        const db = e.target.result;
        if (!db.objectStoreNames.contains(MSG_STORE)) {
          const s = db.createObjectStore(MSG_STORE, { keyPath: 'id' });
          s.createIndex('room_id', 'room_id', { unique: false });
          s.createIndex('ts', 'ts', { unique: false });
        }
        if (!db.objectStoreNames.contains(ROOM_STORE)) {
          db.createObjectStore(ROOM_STORE, { keyPath: 'room_id' });
        }
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
    } catch (_) { resolve(null); }
  });
  return _dbPromise;
};

// Parse a message's server timestamp into an epoch ms for ordering/retention.
const _tsOf = (m) => {
  const t = m && m.timestamp;
  const n = t ? Date.parse(/[Zz]|[+-]\d\d:?\d\d$/.test(t) ? t : t + 'Z') : NaN;
  return Number.isFinite(n) ? n : Date.now();
};

export const idbSaveMessages = async (roomId, msgs) => {
  if (!roomId || !Array.isArray(msgs) || !msgs.length) return;
  const db = await openDB(); if (!db) return;
  try {
    const tx = db.transaction(MSG_STORE, 'readwrite');
    const store = tx.objectStore(MSG_STORE);
    msgs.forEach((m) => {
      if (!m || !m.id) return;
      store.put({ ...m, room_id: roomId, ts: _tsOf(m) });
    });
  } catch (_) { /* ignore */ }
};

export const idbGetMessages = async (roomId) => {
  if (!roomId) return [];
  const db = await openDB(); if (!db) return [];
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(MSG_STORE, 'readonly');
      const idx = tx.objectStore(MSG_STORE).index('room_id');
      const req = idx.getAll(IDBKeyRange.only(roomId));
      req.onsuccess = () => {
        const cutoff = Date.now() - RETENTION_MS;
        const rows = (req.result || [])
          .filter((m) => (m.ts || 0) >= cutoff)
          .sort((a, b) => (a.ts || 0) - (b.ts || 0))
          .map(({ ts, ...m }) => m); // strip internal ts field before returning
        resolve(rows);
      };
      req.onerror = () => resolve([]);
    } catch (_) { resolve([]); }
  });
};

// Delete-for-me / hard removal: drop the local copy entirely.
export const idbDeleteMessage = async (id) => {
  if (!id) return;
  const db = await openDB(); if (!db) return;
  try { db.transaction(MSG_STORE, 'readwrite').objectStore(MSG_STORE).delete(id); } catch (_) { /* ignore */ }
};

// Delete-for-everyone: keep the row but flag it deleted, so an offline reload
// still shows the "deleted" context bubble (never the original content again).
export const idbMarkDeleted = async (id) => {
  if (!id) return;
  const db = await openDB(); if (!db) return;
  try {
    const tx = db.transaction(MSG_STORE, 'readwrite');
    const store = tx.objectStore(MSG_STORE);
    const req = store.get(id);
    req.onsuccess = () => {
      const m = req.result;
      if (m) { m.deleted_for_everyone = true; m.is_deleted_view = true; store.put(m); }
    };
  } catch (_) { /* ignore */ }
};

export const idbSaveRoom = async (roomId, info) => {
  if (!roomId || !info) return;
  const db = await openDB(); if (!db) return;
  try { db.transaction(ROOM_STORE, 'readwrite').objectStore(ROOM_STORE).put({ room_id: roomId, info }); } catch (_) { /* ignore */ }
};

export const idbGetRoom = async (roomId) => {
  if (!roomId) return null;
  const db = await openDB(); if (!db) return null;
  return new Promise((resolve) => {
    try {
      const req = db.transaction(ROOM_STORE, 'readonly').objectStore(ROOM_STORE).get(roomId);
      req.onsuccess = () => resolve(req.result ? req.result.info : null);
      req.onerror = () => resolve(null);
    } catch (_) { resolve(null); }
  });
};

// Background cleanup — drop anything older than the retention window.
export const idbCleanupOld = async () => {
  const db = await openDB(); if (!db) return;
  try {
    const cutoff = Date.now() - RETENTION_MS;
    const tx = db.transaction(MSG_STORE, 'readwrite');
    const store = tx.objectStore(MSG_STORE);
    const req = store.index('ts').openCursor(IDBKeyRange.upperBound(cutoff));
    req.onsuccess = () => {
      const cur = req.result;
      if (cur) { store.delete(cur.primaryKey); cur.continue(); }
    };
  } catch (_) { /* ignore */ }
};

// Wipe everything — called on logout so a different admin on the same device
// never sees the previous admin's cached chats.
export const idbClearAll = async () => {
  const db = await openDB(); if (!db) return;
  try {
    const tx = db.transaction([MSG_STORE, ROOM_STORE], 'readwrite');
    tx.objectStore(MSG_STORE).clear();
    tx.objectStore(ROOM_STORE).clear();
  } catch (_) { /* ignore */ }
};
